interface Row {
  id: string
}

export interface MarkStore<Mark, TRow extends Row> {
  /** Pin the state this client has just confirmed for a row. */
  remember(id: string, mark: Mark): void
  /** Drop a mark whose state was deliberately taken back (undo, rollback). */
  forget(id: string): void
  /** Clears every mark; `resetAllLocalStates` and tests. */
  clear(): void
  /**
   * Run wholesale row replacements (the list refetch, the cross-window cache
   * sync) past the confirmed state. A row that disagrees with its mark is an
   * older read, so the mark's state wins; once a read agrees the mark is
   * dropped and the server is authoritative again.
   */
  reconcile(rows: Array<TRow>): Array<TRow>
}

interface MarkStoreOptions<Mark, TRow extends Row> {
  /**
   * A mark stands in for a server the client has outrun, so it must not pin a
   * row forever. If the server really dropped the state, the mark has to let
   * go. Far longer than any refetch race, far shorter than the session.
   */
  ttlMs: number
  /** The incoming row is behind the confirmed state. */
  isStale: (mark: Mark, row: TRow) => boolean
  /** Re-apply the confirmed state onto a stale row. */
  pin: (mark: Mark, row: TRow) => TRow
}

/**
 * Makes one slice of row state monotonic across writers that replace rows
 * rather than patch them. One module-level store per slice (execution,
 * archive); `execution-marks.ts` describes the race that motivated it.
 */
export function createMarkStore<Mark, TRow extends Row>({
  ttlMs,
  isStale,
  pin,
}: MarkStoreOptions<Mark, TRow>): MarkStore<Mark, TRow> {
  const marks = new Map<string, { mark: Mark; markedAt: number }>()

  const sweepExpired = () => {
    const now = Date.now()
    for (const [id, { markedAt }] of marks) {
      if (now - markedAt > ttlMs) marks.delete(id)
    }
  }

  return {
    remember(id, mark) {
      marks.set(id, { mark, markedAt: Date.now() })
    },
    forget(id) {
      marks.delete(id)
    },
    clear() {
      marks.clear()
    },
    reconcile(rows) {
      if (marks.size === 0) return rows
      sweepExpired()
      if (marks.size === 0) return rows

      let changed = false
      const next = rows.map((row) => {
        const entry = marks.get(row.id)
        if (!entry) return row
        if (!isStale(entry.mark, row)) {
          marks.delete(row.id)
          return row
        }
        changed = true
        return pin(entry.mark, row)
      })
      return changed ? next : rows
    },
  }
}
